import { useState, useEffect, useRef } from 'react';
import { KKH_FEVER_THRESHOLD_C, KKH_FEVER_URL, KKH_HINT_MAX_AGE_HOURS, MS_PER_HOUR } from '../constants.js';
import { DOSAGE_PARAMS, calculateDosage, dailyLimitWarnings, doseSafetyFromHistory, doseSizeWarnings, floorToTenth, nextDoseFromHistory, parseConcentration } from '../rules/dosing.js';
import { concentrationsFor } from '../rules/concentrations.js';
import { notify } from '../lib/notify.js';
import { capitalizeMed, formatClockTime, formatElapsed, formatRemaining, parseEnteredTime, toLocalDatetimeValue } from '../lib/format.js';
import { getDeviceName } from '../lib/device.js';
import { AlertCircle, Save } from '../components/icons.jsx';
import { DoseTracker24h } from '../components/DoseTracker24h.jsx';
import { ProfileWeight } from '../components/ProfileWeight.jsx';
import { PickChildHint } from '../components/PickChildHint.jsx';

export function CalculatorView(props) {
  const { darkMode, medication, setMedication, weight, setWeight, selectedChild, childrenList, concentration, setConcentration, customConcentrations, result, setResult, dosageHistory, temperatureLog = [], saveData, addHistory, otherMedications, undoStack, setUndoStack } = props;

  const [isRegistering, setIsRegistering] = useState(false);
  const [otherMedName, setOtherMedName] = useState('');
  // Medication names, alphabetized — the source of truth for the "Other" dropdown.
  const sortedOtherMedNames = [...otherMedications].map(m => m.name).sort((a, b) => a.localeCompare(b));

  // The same names again, grouped under their category for the dropdown, so a
  // medicine sits under the same heading here as in Records' medicine filter.
  // Missing/blank categories fall under "Other" — the same fallback Records
  // uses, so the two can't disagree about where a medicine belongs.
  //
  // Categories alphabetical, names alphabetical within each; the flat list above
  // stays the source of truth for validity checks, this is presentation only.
  const otherMedGroups = (() => {
    const groups = {};
    for (const m of otherMedications) {
      const cat = (m.category || '').trim() || 'Other';
      (groups[cat] = groups[cat] || []).push(m.name);
    }
    return Object.entries(groups)
      .map(([category, names]) => [category, [...names].sort((a, b) => a.localeCompare(b))])
      .sort(([a], [b]) => a.localeCompare(b));
  })();
  // "Other" medicines carry no dosage calculation, so nothing stops several
  // being given at once — an antihistamine and a rash cream is one trip to the
  // cupboard, and logging it as one action beats repeating the form. The
  // selection is therefore a set, and Record writes one entry per medicine.
  //
  // Nothing is preselected: with one medicine implied by default, a mis-tap on
  // Record logs something nobody chose.
  const [selectedOtherMeds, setSelectedOtherMeds] = useState([]);
  const toggleOtherMed = (name) => {
    setConfirmation('');
    setSelectedOtherMeds(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };
  // Keep the selection valid: a medicine renamed or removed (possibly on the
  // other phone) drops out of it, so a phantom medicine the user never saw
  // can't be recorded.
  useEffect(() => {
    setSelectedOtherMeds(prev => {
      const next = prev.filter(n => sortedOtherMedNames.includes(n));
      return next.length === prev.length ? prev : next;
    });
  }, [otherMedications]);
  const [otherWhen, setOtherWhen] = useState(() => toLocalDatetimeValue(new Date()));
  // See TemperatureView: the datetime field is minute-precision, so an untouched
  // one means "now" and is stamped at full precision rather than parsed back.
  const [otherWhenEdited, setOtherWhenEdited] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  // The child now changes from the banner rather than a picker in this form, so
  // drop what was worked out for the previous child. Skipped on mount: the
  // result lives in MedicationApp and should survive a trip to another tab.
  const prevChild = useRef(selectedChild);
  useEffect(() => {
    if (prevChild.current === selectedChild) return;
    prevChild.current = selectedChild;
    setWeight(''); setResult(null); setConfirmation('');
  }, [selectedChild]);
  const resetOtherTimeToNow =() => { setOtherWhen(toLocalDatetimeValue(new Date())); setOtherWhenEdited(false); };

  // See TemperatureView: keep an untouched field showing the actual current
  // time rather than whenever it was last reset.
  useEffect(() => {
    if (otherWhenEdited) return;
    const id = setInterval(() => setOtherWhen(toLocalDatetimeValue(new Date())), 30000);
    return () => clearInterval(id);
  }, [otherWhenEdited]);
  // The dose the user will actually give — starts at the recommended amount and
  // can be nudged in 0.1 ml steps (or typed) before the dose is registered.
  const [adjustedDose, setAdjustedDose] = useState('');

  // Reset the editable dose to the freshly recommended amount on each calculation.
  useEffect(() => {
    if (result && !result.error) setAdjustedDose(result.mlToGive);
  }, [result]);

  // The next-dose banner states an elapsed time and a wait-until time, so it has
  // to keep moving while the screen sits open — otherwise it would still read
  // "given 25 min ago" an hour later, and would never flip to "can be given now"
  // on its own. Half-minute ticks keep the displayed minute count honest.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setClockTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Reopening the app after more than a minute away counts as coming back to
  // the Calculator: the "✓ recorded" confirmation goes, and with it the
  // next-dose banner it was standing in for comes back (same threshold as
  // MedicationApp's scroll-to-top).
  useEffect(() => {
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt !== null && Date.now() - hiddenAt > 60000) setConfirmation('');
      hiddenAt = null;
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const calc = () => {
    // A new calculation is a fresh re-log attempt: drop the confirmation so the
    // up-to-date warnings (including the "too soon" wait window) can show.
    setConfirmation('');
    const w = selectedChild
      ? parseFloat(childrenList.find(c => c.id === selectedChild)?.weight) 
      : parseFloat(weight);
    // effectiveConc, not concentration: what the Concentration dropdown is
    // actually showing (see below) is what must be dosed against.
    const { mg, ml } = parseConcentration(effectiveConc);

    if (!w || !mg || !ml || w <= 0 || mg <= 0 || ml <= 0) {
      setResult({error: 'Please enter valid values'});
      return;
    }

    const params = DOSAGE_PARAMS[medication];
    // Rounded *down* to the syringe's 0.1 ml marks, never to the nearest: the
    // figure is shown as the "Maximum dose", and rounding to nearest could land
    // just over it (8.875 ml → 8.9 ml = 15.04 mg/kg). Rounding down stays within
    // the guideline range and is never more than 0.1 ml under the target.
    const mlGive = floorToTenth(calculateDosage(w, params.dosePerKg, effectiveConc));
    const totalMg = mlGive * (mg / ml);
    const maxDaily = w * params.maxDailyPerKg;

    // Nothing history-derived is stored on the result any more — the warnings,
    // the 24h total and the register gate are all recomputed at render by
    // doseSafetyFromHistory, so they follow doses synced from the other phone
    // and the passage of time. What's kept here is only what the calculation
    // itself fixes: the recommendation, and the child/weight/concentration it
    // was made for.
    setResult({
      mlToGive: mlGive.toFixed(1),
      mgTotal: totalMg.toFixed(0),
      mgPerMl: mg / ml,
      maxDailyMg: maxDaily.toFixed(0),
      maxMlPerDose: floorToTenth((maxDaily / params.maxDosesPerDay) / mg * ml).toFixed(1),
      maxDoses: params.maxDosesPerDay,
      interval: params.intervalLabel,
      weight: w,
      concentration: `${mg} mg/${ml} ml`,
      childId: selectedChild,
      medication,
    });
  };

  const register = async (isOther = false) => {
    // Checked before the button animates, so a bad time can't leave it stuck.
    const enteredWhen = isOther && otherWhenEdited ? parseEnteredTime(otherWhen) : null;
    if (isOther && otherWhenEdited && !enteredWhen) return;
    setIsRegistering(true);
    await new Promise(r => setTimeout(r, 200));

    if (isOther) {
      if (!selectedChild) {
        notify('Choose a child at the top of the screen first');
        setIsRegistering(false);
        return;
      }
      // Defensive: never record a medication that isn't a real, current option.
      const names = selectedOtherMeds.filter(n => sortedOtherMedNames.includes(n));
      if (names.length === 0) {
        notify('Select a medication');
        setIsRegistering(false);
        return;
      }
      const child = childrenList.find(c => c.id === selectedChild);
      // One timestamp for the whole batch, not one per entry: they were given
      // together, and a shared stamp is what makes them read as one event —
      // Records lists them together and the chart draws them as a single
      // stacked marker with a count.
      const when = (enteredWhen || new Date()).toISOString();
      const entries = names.map(name => ({
        id: crypto.randomUUID(),
        timestamp: when,
        medication: 'other',
        // The Details field is shared, since it describes the one occasion.
        medicationName: otherMedName.trim(),
        medicationType: name,
        weight: child?.weight,
        childName: child?.name,
        childId: selectedChild,
        loggedBy: getDeviceName() || undefined
      }));

      entries.forEach(addHistory);

      // One undo item for the batch, so a single Undo takes back exactly what a
      // single Record put in. entryIds is the real payload; entry/entryId stay
      // for the Records banner and older undo items still on the stack.
      setUndoStack([...undoStack, {
        action: 'add',
        entry: entries[0],
        entryId: entries[0].id,
        entryIds: entries.map(e => e.id),
        entries,
        timestamp: Date.now()
      }]);

      setOtherMedName('');
      setSelectedOtherMeds([]);
      resetOtherTimeToNow();
      setConfirmation(`${names.join(' + ')} recorded successfully`);
    } else {
      const child = selectedChild ? childrenList.find(c => c.id === selectedChild) : null;
      // Use the (possibly adjusted) dose the user chose, falling back to the
      // recommended amount, and recompute the mg total to match.
      // Rounded to the 0.1 ml the record stores *before* the mg is worked out,
      // so a typed 5.25 is saved as 5.3 ml with 5.3 ml's mg, not 5.25's.
      const doseMl = Math.round((parseFloat(adjustedDose) > 0 ? parseFloat(adjustedDose) : parseFloat(result.mlToGive)) * 10) / 10;
      const entry = {
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString(),
        medication: result.medication,
        dose: doseMl.toFixed(1),
        mgTotal: (doseMl * result.mgPerMl).toFixed(0),
        weight: result.weight,
        concentration: result.concentration,
        childName: child?.name,
        childId: selectedChild,
        loggedBy: getDeviceName() || undefined
      };

      addHistory(entry);

      setUndoStack([...undoStack, {
        action: 'add',
        entry: entry,
        entryId: entry.id,
        timestamp: Date.now()
      }]);

      // Confirm the log instead of immediately surfacing the wait warning; the
      // warning only appears if the user tries to calculate/log another dose.
      const medLabel = entry.medication.charAt(0).toUpperCase() + entry.medication.slice(1);
      setResult(null);
      setConfirmation(`${entry.dose} ml of ${medLabel} recorded successfully`);
    }
    setTimeout(() => setIsRegistering(false), 300);
  };

  const allConc = concentrationsFor(customConcentrations, medication);

  // The list can change under the selection — a strength removed in Settings,
  // on this phone or the other one — and a <select> whose value matches no
  // option displays the first option while the state still holds the old value.
  // That would mean calculating against a strength the screen isn't showing, so
  // the selection is validated against the list rather than trusted, and the
  // state is corrected to match what's displayed.
  const effectiveConc = allConc.includes(concentration) ? concentration : (allConc[0] || '');
  useEffect(() => {
    if (effectiveConc !== concentration) setConcentration(effectiveConc);
  }, [effectiveConc, concentration]);

  // A result only holds for the bottle, medicine and weight it was worked out
  // for. Switching the Concentration picker after Calculate used to leave the
  // old millilitres on screen under the new bottle — calculated for 120 mg/5 ml,
  // then switched to 250 mg/5 ml, that's twice the dose. The same goes for a
  // strength removed or a child's weight changed (in Settings, or on the other
  // phone) while the result sits open. Any mismatch drops the result.
  useEffect(() => {
    if (!result || result.error) return;
    const { mg, ml } = parseConcentration(effectiveConc || '/');
    const weightNow = result.childId
      ? parseFloat(childrenList.find(c => c.id === result.childId)?.weight)
      : result.weight;
    if (result.concentration !== `${mg} mg/${ml} ml` || result.medication !== medication || weightNow !== result.weight) {
      setResult(null);
    }
  }, [result, effectiveConc, medication, childrenList]);

  // Dose-dependent safety info, recomputed live as the user adjusts the dose so
  // the warnings, the "safe to administer" line and the register button all react.
  const hasResult = result && !result.error;
  const adjustedMg = hasResult ? (parseFloat(adjustedDose) || 0) * result.mgPerMl : 0;
  // Re-derived every render against the child/weight the result was calculated
  // for (not the current selection, which may have moved on), so the panel and
  // the register gate stay true as history changes underneath.
  const live = hasResult
    ? doseSafetyFromHistory(dosageHistory, result.medication, result.childId, result.weight)
    : null;
  const doseWarning = hasResult
    ? doseSizeWarnings(result.medication, parseFloat(adjustedDose) || 0, adjustedMg, result.mlToGive, result.weight)[0] || null
    : null;
  const liveWarnings = hasResult
    ? [...(doseWarning ? [doseWarning] : []), ...dailyLimitWarnings(live.usedMg, adjustedMg, live.maxDaily), ...live.warnings]
    : [];
  // Every warning gates registering; the timing ones are shown by the banner
  // and the dose-size and 24h ones by the verdict line in the dose box.
  const hasDangerWarning = liveWarnings.some(w => w.type === 'danger');
  const dailyWarning = liveWarnings.find(w => w.daily) || null;
  const shownWarnings = liveWarnings.filter(w => !w.interval && !w.dose && !w.daily);

  // Ambient "when can the next dose be given" hint, shown up front rather than
  // only after Calculate is pressed, so a parent can check at a glance. The
  // rules live in nextDoseFromHistory (tests.html checks them).

  // The weight the safety checks run against: the selected child's, or whatever
  // was typed in for a manual entry.
  const currentWeight = selectedChild
    ? parseFloat(childrenList.find(c => c.id === selectedChild)?.weight)
    : parseFloat(weight);

  const nextDoseInfo = medication === 'other' || !currentWeight
    ? null
    : nextDoseFromHistory(dosageHistory, medication, selectedChild, currentWeight);

  // Latest temperature for the banner's child, if it's recent enough (4 h) to
  // describe the fever now.
  const kkhHint = (() => {
    if (!selectedChild || medication === 'other') return null;
    const latest = temperatureLog
      .filter(e => e.childId === selectedChild)
      .reduce((a, b) => (!a || new Date(b.timestamp) > new Date(a.timestamp) ? b : a), null);
    if (!latest || Date.now() - new Date(latest.timestamp).getTime() > KKH_HINT_MAX_AGE_HOURS * MS_PER_HOUR) return null;
    const t = Number(latest.tempC);
    const fresh = Date.now() - new Date(latest.timestamp).getTime() < 60000;
    const reading = `${t.toFixed(1)} °C ${fresh ? 'just now' : `${formatElapsed(latest.timestamp)} ago`}`;
    const high = t >= KKH_FEVER_THRESHOLD_C;
    // Reading and advice kept apart so the "KKH" between them can be a link.
    const advice = medication === 'paracetamol'
      ? (high
        ? `paracetamol can be 4-hourly above ${KKH_FEVER_THRESHOLD_C} °C`
        : `paracetamol 6-hourly below ${KKH_FEVER_THRESHOLD_C} °C`)
      : (high
        ? `ibuprofen if it stays above ${KKH_FEVER_THRESHOLD_C} °C, 1–2 h after paracetamol`
        : `ibuprofen only if the fever stays above ${KKH_FEVER_THRESHOLD_C} °C`);
    return { reading, advice };
  })();

  return (
    <div className={`rounded-2xl shadow-xl p-3 sm:p-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
      <div className="mb-4">
        <div className={`flex rounded-lg p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <button onClick={() => { setMedication('paracetamol'); setConcentration('100/1'); setResult(null); setConfirmation(''); }}
            className={`flex-1 min-w-0 truncate py-2 px-2 sm:px-4 rounded-md font-medium text-sm sm:text-base transition-all ${
              medication === 'paracetamol' ? 'bg-indigo-600 text-white shadow-sm' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
            }`}>
            Paracetamol
          </button>
          <button onClick={() => { setMedication('ibuprofen'); setConcentration('100/5'); setResult(null); setConfirmation(''); }}
            className={`flex-1 min-w-0 truncate py-2 px-2 sm:px-4 rounded-md font-medium text-sm sm:text-base transition-all ${
              medication === 'ibuprofen' ? 'bg-indigo-600 text-white shadow-sm' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
            }`}>
            Ibuprofen
          </button>
          <button onClick={() => { setMedication('other'); setResult(null); setConfirmation(''); }}
            className={`flex-1 min-w-0 truncate py-2 px-2 sm:px-4 rounded-md font-medium text-sm sm:text-base transition-all ${
              medication === 'other' ? 'bg-indigo-600 text-white shadow-sm' : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
            }`}>
            Other
          </button>
        </div>
      </div>

      {/* The one place timing is reported: it merges what used to be this
          banner and the TOO SOON / CROSS-MED / CAUTION boxes under the result.
          Those warnings still block "Register" — they just aren't repeated.
          The all-clear version steps aside once a dose is calculated: the dose
          box's "✓ Safe to administer" says the same thing, and the room is
          needed. Too-soon and caution stay up regardless — except right
          after a dose is registered: the "✓ recorded" confirmation is the
          news then, and a red TOO SOON for the dose just given reads as an
          error. The banner returns with the next look at the Calculator
          (another tab and back, a new Calculate, a different medicine or
          child, or reopening the app after a while). */}
      {nextDoseInfo && !confirmation && !(hasResult && nextDoseInfo.available && !nextDoseInfo.caution) && (
        <div className={`rounded-lg px-3 py-2 mb-3 text-sm text-center font-medium ${
          !nextDoseInfo.available
            // Toned down from a solid red with a thick border: the text still
            // says TOO SOON, and Register is blocked regardless.
            ? (darkMode ? 'bg-red-900/40 text-red-200 border border-red-800/60' : 'bg-red-50/70 text-red-800 border border-red-200')
            : nextDoseInfo.caution
              ? (darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-50 text-yellow-800')
              : (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-50 text-green-800')
        }`}>
          {!nextDoseInfo.available
            ? <>
                {/* One wording whichever medicine is the one in the way (this
                    one's own interval, or the other's cross-medication gap),
                    and the time alone: the wait is at most 6 h, so the date
                    only cost a line. */}
                <span className="font-bold">⚠️ TOO SOON</span>{' — '}
                {`${capitalizeMed(nextDoseInfo.med)} was given ${formatElapsed(nextDoseInfo.given)} ago. Wait until ${formatClockTime(nextDoseInfo.time)} (in ${formatRemaining(nextDoseInfo.time)}) before another dose.`}
              </>
            : nextDoseInfo.caution
              ? `⏱️ ${capitalizeMed(medication)} can be given now, but the last one was only ${formatElapsed(new Date(nextDoseInfo.lastSame.timestamp))} ago.`
              : `✓ Next ${medication} dose can be given now`}
        </div>
      )}

      {/* KKH's fever advice, keyed to the child's latest reading: paracetamol
          4-hourly above 38.5 °C and 6-hourly below; ibuprofen only while the
          fever stays above 38.5 °C, 1–2 h after paracetamol. Advice only — the
          app's own interval rules still decide what Register allows. Shown
          while deciding; it steps aside once a dose is calculated, like the
          all-clear banner, so the Calculator still fits one screen. */}
      {!hasResult && kkhHint && (
        <p className={`text-xs mb-3 px-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          🌡 {kkhHint.reading} —{' '}
          <a href={KKH_FEVER_URL} target="_blank" rel="noopener noreferrer"
            className={`underline ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>KKH</a>
          : {kkhHint.advice}
        </p>
      )}

      {medication !== 'other' && (
        <DoseTracker24h
          darkMode={darkMode}
          dosageHistory={dosageHistory}
          selectedChild={selectedChild}
          weight={currentWeight}
          pending={hasResult ? { med: result.medication, mg: adjustedMg } : null}
        />
      )}

      {confirmation && (
        <div className={`border rounded-lg p-4 mb-6 ${darkMode ? 'bg-green-900 border-green-700' : 'bg-green-50 border-green-200'}`}>
          <p className={`font-semibold text-center ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
            ✓ {confirmation}
          </p>
        </div>
      )}

      {medication === 'other' ? (
        <div className="space-y-4 mb-6">
          {childrenList.length > 0 ? (
            !selectedChild && <PickChildHint darkMode={darkMode} />
          ) : (
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-50 text-yellow-800'}`}>
              <p className="text-sm">Add a child profile in Settings first.</p>
            </div>
          )}

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Medication *
            </label>
            {sortedOtherMedNames.length === 0 ? (
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No medicines configured — add one in Settings → Meds.
              </p>
            ) : (
              /* Same shape as Records' medicine filter: category heading, then
                 its medicines as chips. Tapping toggles, so a whole evening's
                 medicines go in as one record. One category is no grouping at
                 all — a lone heading over every name is just noise for anyone
                 who never set categories. */
              <div className="space-y-3">
                {(otherMedGroups.length > 1 ? otherMedGroups : [[null, sortedOtherMedNames]]).map(([category, names]) => (
                  <div key={category || 'all'}>
                    {category && (
                      <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {category}
                      </p>
                    )}
                    <div className={`flex flex-wrap gap-2 ${category ? 'pl-6' : ''}`}>
                      {names.map(name => {
                        const checked = selectedOtherMeds.includes(name);
                        return (
                          <button key={name} type="button" onClick={() => toggleOtherMed(name)}
                            aria-pressed={checked}
                            className={`px-3 py-2 rounded-lg text-sm border transition ${
                              checked
                                ? 'bg-indigo-600 border-indigo-600 text-white'
                                : darkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                            }`}>
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Details (Optional)
            </label>
            <input type="text" value={otherMedName} onChange={(e) => setOtherMedName(e.target.value)}
              placeholder="e.g., Amoxicillin 250mg"
              className={`w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                darkMode ? 'bg-gray-700 border border-gray-600 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
              }`} />
            {/* One Details field, several medicines: say so rather than let
                someone type "250mg" meaning only one of them. */}
            {selectedOtherMeds.length > 1 && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Saved with all {selectedOtherMeds.length} selected medicines.
              </p>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Date &amp; Time
            </label>
            <div className="flex gap-2">
              <input type="datetime-local" value={otherWhen} onChange={(e) => { setOtherWhen(e.target.value); setOtherWhenEdited(true); }}
                className={`flex-1 min-w-0 px-3 py-2 sm:px-4 sm:py-3 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  darkMode ? 'bg-gray-700 border border-gray-600 text-white' : 'bg-white border border-gray-300 text-gray-900'
                }`} />
              <button type="button" onClick={resetOtherTimeToNow}
                className={`flex-shrink-0 px-4 py-2 sm:py-3 rounded-lg font-medium transition ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}>
                Now
              </button>
            </div>
          </div>

          {/* Nothing selected is now a real state (nothing is preselected), so
              the button waits for a choice rather than alerting after the tap. */}
          <button onClick={() => register(true)}
            disabled={isRegistering || !selectedChild || selectedOtherMeds.length === 0}
            className={`w-full font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 ${
              !selectedChild || selectedOtherMeds.length === 0
                ? 'bg-gray-500 cursor-not-allowed opacity-50'
                : isRegistering
                ? 'bg-indigo-700 scale-95'
                : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
            }`}>
            <Save className={`w-4 h-4 ${isRegistering ? 'animate-pulse' : ''}`} />
            {isRegistering
              ? 'Recording...'
              : selectedOtherMeds.length > 1
              ? `Record ${selectedOtherMeds.length} Medications`
              : 'Record Medication'}
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-4">
            {/* With a child picked in the banner the weight comes from their
                profile, so one line says so instead of a disabled field. With
                no child it's a manual weight entry. */}
            {/* key: an open weight edit belongs to one child — switching child
                in the banner mustn't carry the half-typed value across. */}
            {selectedChild ? (
              <ProfileWeight key={selectedChild} darkMode={darkMode} childrenList={childrenList} saveData={saveData}
                child={childrenList.find(c => c.id === selectedChild)}
                onChanged={() => { setResult(null); setConfirmation(''); }} />
            ) : (
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Child's Weight (kg)
                </label>
                <input type="number" inputMode="decimal"
                  value={weight}
                  onChange={(e) => { setWeight(e.target.value); setResult(null); setConfirmation(''); }}
                  placeholder="e.g., 15"
                  className={`w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                    darkMode ? 'bg-gray-700 border border-gray-600 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400'
                  }`} />
              </div>
            )}

            {/* Concentration and Calculate share a row: picking the bottle is
                the last step before calculating, so they read as one action. */}
            <div>
              <label htmlFor="concentration" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Concentration
              </label>
              <div className="flex gap-2">
                <select id="concentration" value={effectiveConc} onChange={(e) => { setConcentration(e.target.value); setConfirmation(''); }}
                  className={`flex-1 min-w-0 px-3 py-2 sm:px-4 sm:py-3 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                    darkMode ? 'bg-gray-700 border border-gray-600 text-white' : 'bg-white border border-gray-300 text-gray-900'
                  }`}>
                  {allConc.map(c => {
                    const parts = c.split('/');
                    return <option key={c} value={c}>{parts[0]} mg/{parts[1]} ml</option>;
                  })}
                </select>
                <button onClick={calc}
                  className="flex-shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition shadow-md">
                  Calculate<span className="hidden sm:inline"> Dosage</span>
                </button>
              </div>
            </div>
          </div>

          {result && !result.error && (
            <div className="space-y-3">
              {shownWarnings.length > 0 && (
                <div className="space-y-2">
                  {shownWarnings.map((w, i) => (
                    <div key={i} className={`border-2 rounded-lg p-3 ${
                      w.type === 'danger'
                        ? darkMode ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-300'
                        : w.type === 'warning'
                        ? darkMode ? 'bg-orange-900 border-orange-700' : 'bg-orange-50 border-orange-300'
                        : darkMode ? 'bg-yellow-900 border-yellow-700' : 'bg-yellow-50 border-yellow-300'
                    }`}>
                      <p className={`font-semibold text-sm ${
                        w.type === 'danger'
                          ? darkMode ? 'text-red-200' : 'text-red-800'
                          : w.type === 'warning'
                          ? darkMode ? 'text-orange-200' : 'text-orange-800'
                          : darkMode ? 'text-yellow-200' : 'text-yellow-800'
                      }`}>
                        {w.message}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* One box for "what we recommend" and "what you'll give": the
                  amount starts at the recommendation and −/+ adjust it in place.
                  Its label is a caption inside the box rather than a heading
                  over it, and the verdict colours the number itself. */}
              {(() => {
                const adjusted = parseFloat(adjustedDose) !== parseFloat(result.mlToGive);
                const timingDanger = live.warnings.some(w => w.interval && w.type === 'danger');
                // Most severe first: what's wrong with this amount, then the
                // 24h total, then timing (spelled out in the banner), then a
                // merely-high dose.
                const verdict =
                    !(parseFloat(adjustedDose) > 0) ? { tone: 'red', text: 'Enter a dose' }
                  : doseWarning && doseWarning.level === 'over' ? { tone: 'red', text: `✗ ${doseWarning.message}` }
                  : live.remainingMg < adjustedMg ? { tone: 'red', text: `✗ Over the 24 h limit — only ${Math.max(0, live.remainingMg).toFixed(0)} mg left` }
                  : timingDanger ? { tone: 'red', text: '✗ Too soon — see the banner above' }
                  : hasDangerWarning ? { tone: 'red', text: '✗ Not safe to give — see the warning above' }
                  : doseWarning ? { tone: 'yellow', text: `⚠ ${doseWarning.message}` }
                  : dailyWarning ? { tone: 'yellow', text: `⚠ ${dailyWarning.message}` }
                  : { tone: 'green', text: '✓ Safe to administer' };
                const toneText = {
                  green: darkMode ? 'text-green-400' : 'text-green-700',
                  yellow: darkMode ? 'text-yellow-300' : 'text-yellow-700',
                  red: darkMode ? 'text-red-400' : 'text-red-600',
                }[verdict.tone];
                const stepBtn = `w-11 h-11 flex-shrink-0 rounded-lg font-bold text-2xl flex items-center justify-center transition active:scale-95 ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`;
                return (
                  <div className={`border rounded-lg px-3 pt-2 pb-3 ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {adjusted ? 'Adjusted dose' : 'Maximum dose'}
                      </span>
                      {adjusted && (
                        <button type="button" onClick={() => setAdjustedDose(result.mlToGive)}
                          className={`text-xs font-medium underline ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                          Reset to {result.mlToGive} ml
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      <button type="button" aria-label="Decrease dose by 0.1 ml" className={stepBtn}
                        onClick={() => setAdjustedDose(d => Math.max(0, parseFloat(d || 0) - 0.1).toFixed(1))}>
                        −
                      </button>
                      <div className="flex items-baseline gap-1">
                        <input type="number" step="0.1" min="0" inputMode="decimal"
                          value={adjustedDose} onChange={(e) => setAdjustedDose(e.target.value)}
                          aria-label="Dose to give in ml"
                          className={`w-24 text-center text-3xl font-bold px-1 py-0.5 rounded-lg bg-transparent focus:ring-2 focus:ring-indigo-500 ${toneText} ${
                            darkMode ? 'border border-gray-700' : 'border border-gray-200'
                          }`} />
                        <span className={`text-xl font-bold ${toneText}`}>ml</span>
                      </div>
                      <button type="button" aria-label="Increase dose by 0.1 ml" className={stepBtn}
                        onClick={() => setAdjustedDose(d => (parseFloat(d || 0) + 0.1).toFixed(1))}>
                        +
                      </button>
                    </div>
                    <p className={`text-sm text-center mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {adjustedMg.toFixed(0)} mg ·{' '}
                      <span className={`font-semibold ${toneText}`} data-verdict={verdict.tone}>{verdict.text}</span>
                    </p>
                    {/* One line instead of four bullets. The daily mg limit isn't
                        repeated: the Last 24 Hours tracker shows it ("… / 426 mg"). */}
                    <p className={`text-xs sm:text-sm text-center mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Max {result.maxDoses} doses per day, {result.maxMlPerDose} ml each,{' '}
                      <span className="whitespace-nowrap">every {result.interval.replace(/\s*hours?$/, 'h').replace('-', '–')}</span>
                    </p>
                  </div>
                );
              })()}

              <button onClick={() => register(false)}
                disabled={isRegistering || !(parseFloat(adjustedDose) > 0) || hasDangerWarning}
                className={`w-full font-semibold py-2 px-4 rounded-lg transition-all flex items-center justify-center gap-2 ${
                  hasDangerWarning || !(parseFloat(adjustedDose) > 0)
                    ? 'bg-gray-500 cursor-not-allowed opacity-50'
                    : isRegistering
                    ? 'bg-green-700 scale-95'
                    : 'bg-green-600 hover:bg-green-700 active:scale-95'
                }`}>
                <Save className={`w-4 h-4 ${isRegistering ? 'animate-pulse' : ''}`} />
                {isRegistering
                  ? 'Registering...'
                  : hasDangerWarning
                  ? 'Cannot Register (Unsafe)'
                  : 'Register Dosage'}
              </button>
            </div>
          )}

          {result && result.error && (
            <div className={`border-2 rounded-lg p-4 ${darkMode ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200'}`}>
              <p className={`font-medium ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                {result.error}
              </p>
            </div>
          )}

          {/* Folded by default: the rules are reference, not something to
              read on every dose, and open they filled most of the first screen.
              Muted: a line of amber text rather than a coloured box, so it
              doesn't compete with the banner and dose box for attention. */}
          <details className={`group mt-4 px-1 ${darkMode ? 'text-yellow-500/80' : 'text-yellow-700/80'}`}>
            <summary className="flex items-center gap-1.5 cursor-pointer text-xs list-none">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="group-open:hidden">Click for dosing rules &amp; safety</span>
              <span className="hidden group-open:inline">Hide dosing rules &amp; safety</span>
            </summary>
            <div className="flex items-start mt-2">
              <div className="text-xs">
                <ul className="space-y-1 list-disc list-inside">
                  <li>Paracetamol: {DOSAGE_PARAMS.paracetamol.dosePerKg} mg/kg per dose, max {DOSAGE_PARAMS.paracetamol.maxDailyPerKg} mg/kg/day</li>
                  <li>Ibuprofen: {DOSAGE_PARAMS.ibuprofen.dosePerKg} mg/kg per dose, max {DOSAGE_PARAMS.ibuprofen.maxDailyPerKg} mg/kg/day</li>
                  <li>Always consult a healthcare provider</li>
                  <li>Do not exceed recommended dosages</li>
                  <li>For children under 6 months, consult a doctor</li>
                  {medication === 'ibuprofen' && (
                    <li>Ibuprofen not for children under 6 months</li>
                  )}
                </ul>
              </div>
            </div>
          </details>
        </>
      )}
    </div>
  );
}
